import { isTrellisTheme } from 'foundations-theming/utils';
import { fwcColor } from 'foundations-theming/utils/fwc';
import {
  ButtonSizes,
  ButtonStateStyles,
  ButtonUses,
  SizeTokens,
  VariantTokens,
} from './Button.types';
import type { Theme } from 'foundations-theming';

/** @returns true if the button looks like a `<button>` */
export const isButtonUse = (use: ButtonUses) =>
  use === 'primary' ||
  use === 'primary-white' ||
  use === 'primary-on-fill' ||
  use === 'secondary' ||
  use === 'secondary-ghost' ||
  use === 'secondary-alt' ||
  use === 'tertiary' ||
  use === 'tertiary-light' ||
  use === 'tertiary-extra-light' ||
  use === 'tertiary-on-fill' ||
  use === 'tertiary-transparent' ||
  use === 'danger' ||
  use === 'transparent' ||
  use === 'view-group';

/** @returns true if the button looks like a form `<input>` */
export const isFormUse = (use: ButtonUses) =>
  use === 'form' || use === 'form-on-dark';

/** @returns true if the button looks like a link `<a>` */
export const isLinkUse = (use: ButtonUses) =>
  use === 'link' ||
  use === 'link-danger' ||
  use === 'link-on-dark' ||
  use === 'link-on-bright' ||
  use === 'link-on-fill' ||
  use === 'control' ||
  use === 'control-on-bright' ||
  use === 'control-on-dark' ||
  use === 'control-danger' ||
  use === 'control-on-fill';

export const isControlUse = (use: ButtonUses) =>
  use === 'control' ||
  use === 'control-on-bright' ||
  use === 'control-on-dark' ||
  use === 'control-danger' ||
  use === 'control-on-fill';

/** @returns true if the button has use `transparent` */
export const isTransparentUse = (use: ButtonUses) => use === 'transparent';

/** @returns true if the button looks like an unstyled `<div>` */
export const isUnstyledUse = (use: ButtonUses) => use === 'unstyled';

/**
 * These styles are used by the following components and styled-components wrappers:
 *
 * - PrivateButton: StyledButtonTextContainer
 * - LoadingButton: StyledLoadingButtonContent
 *
 * Make sure to read the PR below if you consider changing the display property.
 *
 * @see https://git.hubteam.com/HubSpot/foundations-components/pull/6634
 */
export const getSharedButtonContainerStyles = (
  theme: Theme,
  use: ButtonUses
) => ({
  display: 'flex',
  // link variant Buttons should behave like the Link component, which does not provide default spacing for children
  gap: isLinkUse(use) ? theme.space['0'] : theme.space['200'],
  alignItems: 'center',
  inlineSize: '100%',
  blockSize: '100%',
});

const sharedDisabledTokens: ButtonStateStyles = {
  backgroundColor: fwcColor('fwc-button-fill-primary-disabled'),
  borderColor: fwcColor('fwc-button-border-primary-disabled'),
  color: fwcColor('fwc-button-text-primary-disabled'),
  cursor: 'auto',
};

const tertiaryLightStyles: VariantTokens = {
  idle: {
    backgroundColor: fwcColor('fwc-button-fill-tertiary-default'),
    borderColor: fwcColor('fwc-button-border-tertiary-default'),
    color: fwcColor('fwc-button-text-tertiary-default'),
  },
  hover: {
    backgroundColor: fwcColor('fwc-button-fill-tertiary-hover'),
    borderColor: fwcColor('fwc-button-border-tertiary-default'),
    color: fwcColor('fwc-button-text-tertiary-default'),
    textDecoration: 'none',
  },
  active: {
    backgroundColor: fwcColor('fwc-button-fill-tertiary-pressed'),
    borderColor: fwcColor('fwc-button-border-tertiary-default'),
    color: fwcColor('fwc-button-text-tertiary-default'),
  },
  disabled: {
    backgroundColor: fwcColor('fwc-button-fill-tertiary-disabled'),
    borderColor: fwcColor('fwc-button-border-tertiary-disabled'),
    color: fwcColor('fwc-button-text-tertiary-disabled'),
    cursor: 'auto',
  },
};

export const setAsActive = (useTokens: VariantTokens): VariantTokens => {
  useTokens.idle = useTokens.active;
  return useTokens;
};

export const getUseTokens = (use: ButtonUses): VariantTokens => {
  const tokens: Record<ButtonUses, VariantTokens> = {
    primary: {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-primary-default'),
        borderColor: fwcColor('fwc-button-border-primary-default'),
        color: fwcColor('fwc-button-text-primary-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-primary-hover'),
        borderColor: fwcColor('fwc-button-border-primary-default'),
        color: fwcColor('fwc-button-text-primary-default'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-primary-pressed'),
        borderColor: fwcColor('fwc-button-border-primary-default'),
        color: fwcColor('fwc-button-text-primary-default'),
      },
      disabled: {
        ...sharedDisabledTokens,
      },
    },
    'primary-white': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-inverse-default-alt'),
        borderColor: fwcColor('fwc-button-border-inverse-default-alt'),
        color: fwcColor('fwc-button-text-inverse-default-alt'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-inverse-hover-alt'),
        borderColor: fwcColor('fwc-button-border-inverse-default-alt'),
        color: fwcColor('fwc-button-text-inverse-default-alt'),
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-inverse-pressed-alt'),
        borderColor: fwcColor('fwc-button-border-inverse-default-alt'),
        color: fwcColor('fwc-button-text-inverse-default-alt'),
      },
      disabled: {
        backgroundColor: fwcColor('fwc-button-fill-inverse-disabled-alt'),
        borderColor: fwcColor('fwc-button-border-inverse-disabled-alt'),
        color: fwcColor('fwc-button-text-inverse-disabled-alt'),
        cursor: 'auto',
      },
    },
    'primary-on-fill': {
      idle: {
        backgroundColor: fwcColor(
          'fwc-button-specialty-primary-on-fill-default'
        ),
        borderColor: fwcColor('fwc-button-border-primary-default'),
        color: fwcColor('fwc-button-text-inverse-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-specialty-primary-on-fill-hover'),
        borderColor: fwcColor('fwc-button-border-primary-default'),
        color: fwcColor('fwc-button-text-inverse-default'),
      },
      active: {
        backgroundColor: fwcColor(
          'fwc-button-specialty-primary-on-fill-pressed'
        ),
        borderColor: fwcColor('fwc-button-border-primary-default'),
        color: fwcColor('fwc-button-text-inverse-default'),
      },
      disabled: {
        backgroundColor: fwcColor(
          'fwc-button-specialty-primary-on-fill-disabled'
        ),
        borderColor: fwcColor('fwc-button-border-primary-disabled'),
        color: fwcColor('fwc-button-text-core-disabled'),
      },
    },
    secondary: {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-secondary-default'),
        borderColor: fwcColor('fwc-button-border-secondary-default'),
        color: fwcColor('fwc-button-text-secondary-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-secondary-hover'),
        borderColor: fwcColor('fwc-button-border-secondary-default'),
        color: fwcColor('fwc-button-text-secondary-default'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-secondary-pressed'),
        borderColor: fwcColor('fwc-button-border-secondary-default'),
        color: fwcColor('fwc-button-text-secondary-default'),
      },
      disabled: {
        backgroundColor: fwcColor('fwc-button-fill-secondary-disabled'),
        borderColor: fwcColor('fwc-button-border-secondary-disabled'),
        color: fwcColor('fwc-button-text-secondary-disabled'),
        cursor: 'auto',
      },
    },
    'secondary-ghost': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-inverse-default'),
        borderColor: fwcColor('fwc-button-border-inverse-default'),
        color: fwcColor('fwc-button-text-inverse-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-inverse-hover'),
        borderColor: fwcColor('fwc-button-border-inverse-default'),
        color: fwcColor('fwc-button-text-inverse-default'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-inverse-pressed'),
        borderColor: fwcColor('fwc-button-border-inverse-default'),
        color: fwcColor('fwc-button-text-inverse-default'),
      },
      disabled: {
        backgroundColor: fwcColor('fwc-button-fill-inverse-disabled'),
        borderColor: fwcColor('fwc-button-border-inverse-disabled'),
        color: fwcColor('fwc-button-text-core-disabled'),
        cursor: 'auto',
      },
    },
    'secondary-alt': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-secondary-default-alt'),
        borderColor: fwcColor('fwc-button-border-secondary-default-alt'),
        color: fwcColor('fwc-button-text-secondary-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-secondary-hover-alt'),
        borderColor: fwcColor('fwc-button-border-secondary-default-alt'),
        color: fwcColor('fwc-button-text-secondary-default'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-secondary-pressed-alt'),
        borderColor: fwcColor('fwc-button-border-secondary-default-alt'),
        color: fwcColor('fwc-button-text-secondary-default'),
      },
      disabled: {
        backgroundColor: fwcColor('fwc-button-fill-secondary-disabled-alt'),
        borderColor: fwcColor('fwc-button-border-secondary-disabled-alt'),
        color: fwcColor('fwc-button-text-secondary-disabled'),
        cursor: 'auto',
      },
    },
    tertiary: {
      idle: {
        backgroundColor: fwcColor(
          'fwc-button-fill-transitional-tertiary-default'
        ),
        borderColor: fwcColor(
          'fwc-button-border-transitional-tertiary-default'
        ),
        color: fwcColor('fwc-button-text-inverse-default'),
      },
      hover: {
        backgroundColor: fwcColor(
          'fwc-button-fill-transitional-tertiary-hover'
        ),
        borderColor: fwcColor(
          'fwc-button-border-transitional-tertiary-default'
        ),
        color: fwcColor('fwc-button-text-inverse-default'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor(
          'fwc-button-fill-transitional-tertiary-pressed'
        ),
        borderColor: fwcColor(
          'fwc-button-border-transitional-tertiary-default'
        ),
        color: fwcColor('fwc-button-text-inverse-default'),
      },
      disabled: {
        backgroundColor: fwcColor('fwc-button-fill-tertiary-disabled'),
        borderColor: fwcColor('fwc-button-border-tertiary-disabled'),
        color: fwcColor('fwc-button-text-tertiary-disabled'),
        cursor: 'auto',
      },
    },
    'tertiary-light': {
      ...tertiaryLightStyles,
    },
    'tertiary-extra-light': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-field-default-alt'),
        borderColor: fwcColor('fwc-button-border-tertiary-default'),
        color: fwcColor('fwc-button-text-tertiary-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-tertiary-hover'),
        borderColor: fwcColor('fwc-button-border-tertiary-default'),
        color: fwcColor('fwc-button-text-tertiary-default'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-tertiary-pressed'),
        borderColor: fwcColor('fwc-button-border-tertiary-default'),
        color: fwcColor('fwc-button-text-tertiary-default'),
      },
      disabled: {
        backgroundColor: fwcColor('fwc-button-fill-tertiary-disabled'),
        borderColor: fwcColor('fwc-button-border-tertiary-disabled'),
        color: fwcColor('fwc-button-text-tertiary-disabled'),
        cursor: 'auto',
      },
    },
    'tertiary-transparent': {
      idle: {
        backgroundColor: fwcColor(
          'fwc-button-specialty-tertiary-transparent-default'
        ),
        borderColor: fwcColor('fwc-button-border-tertiary-default'),
        color: fwcColor('fwc-button-text-core-default'),
      },
      hover: {
        backgroundColor: fwcColor(
          'fwc-button-specialty-tertiary-transparent-hover'
        ),
        borderColor: fwcColor('fwc-button-border-tertiary-default'),
        color: fwcColor('fwc-button-text-core-default'),
      },
      active: {
        backgroundColor: fwcColor(
          'fwc-button-specialty-tertiary-transparent-pressed'
        ),
        borderColor: fwcColor('fwc-button-border-tertiary-default'),
        color: fwcColor('fwc-button-text-core-default'),
      },
      disabled: {
        backgroundColor: fwcColor(
          'fwc-button-specialty-tertiary-transparent-disabled'
        ),
        borderColor: fwcColor('fwc-button-border-tertiary-disabled'),
        color: fwcColor('fwc-button-text-tertiary-disabled'),
        cursor: 'auto',
      },
    },
    'tertiary-on-fill': {
      idle: {
        backgroundColor: fwcColor(
          'fwc-button-specialty-tertiary-on-fill-default'
        ),
        borderColor: fwcColor('fwc-button-border-tertiary-default'),
        color: fwcColor('fwc-button-text-inverse-default-alt'),
      },
      hover: {
        backgroundColor: fwcColor(
          'fwc-button-specialty-tertiary-on-fill-hover'
        ),
        borderColor: fwcColor('fwc-button-border-tertiary-default'),
        color: fwcColor('fwc-button-text-inverse-default-alt'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor(
          'fwc-button-specialty-tertiary-on-fill-pressed'
        ),
        borderColor: fwcColor('fwc-button-border-tertiary-default'),
        color: fwcColor('fwc-button-text-inverse-default-alt'),
      },
      disabled: {
        backgroundColor: fwcColor(
          'fwc-button-specialty-tertiary-on-fill-disabled'
        ),
        borderColor: fwcColor('fwc-button-border-core-hover'),
        color: fwcColor('fwc-button-text-tertiary-disabled'),
        cursor: 'auto',
      },
    },
    danger: {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-alert-default'),
        borderColor: fwcColor('fwc-button-fill-alert-default'),
        color: fwcColor('fwc-button-text-alert-on-fill-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-alert-hover'),
        borderColor: fwcColor('fwc-button-fill-alert-hover'),
        color: fwcColor('fwc-button-text-alert-on-fill-default'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-alert-pressed'),
        borderColor: fwcColor('fwc-button-fill-alert-pressed'),
        color: fwcColor('fwc-button-text-alert-on-fill-default'),
      },
      disabled: {
        ...sharedDisabledTokens,
      },
    },
    transparent: {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-text-interactive-alt-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-text-interactive-alt-hover'),
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-text-interactive-alt-pressed'),
      },
      disabled: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-interactive-default-disabled'),
      },
    },
    unstyled: {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-surface-default'),
        borderColor: fwcColor('fwc-button-border-core-on-fill-default'),
        color: fwcColor('fwc-button-text-core-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-surface-default'),
        borderColor: fwcColor('fwc-button-border-core-on-fill-default'),
        color: fwcColor('fwc-button-text-core-default'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-surface-default'),
        borderColor: fwcColor('fwc-button-border-core-on-fill-default'),
        color: fwcColor('fwc-button-text-core-default'),
      },
      disabled: {
        ...sharedDisabledTokens,
      },
    },
    form: {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-surface-raised'),
        borderColor: fwcColor('fwc-button-border-core-default'),
        color: fwcColor('fwc-button-text-core-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-surface-raised'),
        borderColor: fwcColor('fwc-button-border-core-default'),
        color: fwcColor('fwc-button-text-core-default'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-surface-raised'),
        borderColor: fwcColor('fwc-button-border-core-default'),
        color: fwcColor('fwc-button-text-core-default'),
      },
      disabled: {
        ...sharedDisabledTokens,
      },
    },
    'form-on-dark': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-surface-default'),
        borderColor: fwcColor('fwc-button-border-core-default'),
        color: fwcColor('fwc-button-text-core-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-surface-default'),
        borderColor: fwcColor('fwc-button-border-core-default'),
        color: fwcColor('fwc-button-text-core-default'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-surface-default'),
        borderColor: fwcColor('fwc-button-border-core-default'),
        color: fwcColor('fwc-button-text-core-default'),
      },
      disabled: {
        ...sharedDisabledTokens,
      },
    },
    link: {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-interactive-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-interactive-default-hover'),
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-interactive-default-pressed'),
      },
      disabled: {
        ...sharedDisabledTokens,
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-interactive-default-disabled'),
      },
    },
    'link-danger': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-alert-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-alert-hover'),
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-alert-pressed'),
      },
      disabled: {
        ...sharedDisabledTokens,
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-interactive-default-disabled'),
      },
    },
    'link-on-dark': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-link-on-fill-alt-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-interactive-default-hover'),
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-link-on-fill-alt-pressed'),
      },
      disabled: {
        ...sharedDisabledTokens,
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-button-text-inverse-disabled'),
      },
    },
    'link-on-bright': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-text-core-alt-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-interactive-default-hover'),
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-interactive-default-pressed'),
      },
      disabled: {
        ...sharedDisabledTokens,
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-interactive-default-disabled'),
      },
    },
    'link-on-fill': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-link-on-fill-default'),
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-link-on-fill-hover'),
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-link-on-fill-pressed'),
      },
      disabled: {
        ...sharedDisabledTokens,
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-link-on-fill-disabled'),
      },
    },
    'view-group': {
      ...tertiaryLightStyles,
    },
    control: {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-text-interactive-alt-default'),
        textDecoration: 'none',
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-text-interactive-alt-hover'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-text-interactive-alt-pressed'),
        textDecoration: 'none',
      },
      disabled: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-interactive-default-disabled'),
        textDecoration: 'none',
      },
    },
    'control-on-dark': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-button-text-inverse-default'),
        textDecoration: 'none',
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-button-text-inverse-hover'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-button-text-inverse-pressed'),
        textDecoration: 'none',
      },
      disabled: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-button-text-inverse-disabled'),
        textDecoration: 'none',
      },
    },
    'control-on-bright': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-button-text-core-default'),
        textDecoration: 'none',
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-text-interactive-alt-hover'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-text-interactive-alt-pressed'),
        textDecoration: 'none',
      },
      disabled: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-button-text-core-disabled'),
        textDecoration: 'none',
      },
    },
    'control-danger': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-alert-default'),
        textDecoration: 'none',
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-alert-hover'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-alert-pressed'),
        textDecoration: 'none',
      },
      disabled: {
        ...sharedDisabledTokens,
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-text-interactive-default-disabled'),
        textDecoration: 'none',
      },
    },
    'control-on-fill': {
      idle: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-link-on-fill-default'),
        textDecoration: 'none',
      },
      hover: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-link-on-fill-hover'),
        textDecoration: 'none',
      },
      active: {
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-link-on-fill-pressed'),
        textDecoration: 'none',
      },
      disabled: {
        ...sharedDisabledTokens,
        backgroundColor: fwcColor('fwc-button-fill-transparent-default'),
        borderColor: fwcColor('fwc-button-fill-transparent-default'),
        color: fwcColor('fwc-link-specialty-link-on-fill-disabled'),
        textDecoration: 'none',
      },
    },
  };

  return tokens[use];
};

export const shouldButtonBeUnderlined = (use: ButtonUses, theme: Theme) =>
  isTrellisTheme(theme) && isLinkUse(use) && !isControlUse(use);

/**
 * This helper retrieves size tokens for a particular size (extra-small, small, medium).
 *
 * @param size - The current variant of the Button.
 * @param use - The current variant of the Button.
 *
 * @returns A new object containing the typography, padding, and gap tokens for a particular size.
 */
export const getSizeTokens = (
  size: ButtonSizes,
  use: ButtonUses,
  floatingGroup?: boolean
): SizeTokens => {
  /**
   * Tokens for common properties across variants
   *
   * iconSize follows the same sizing increments as the ones that were applied separately to ExternalLinkIcon.
   * This ensures the same height between PrivateButtonLinks with non-ExternalLinkIcon icons (using hideExternalLinkIcon prop)
   * and PrivateButtonLinks with non-ExternalLinkIcon icons AND ExternalLinkIcon.
   */
  const tokens: Record<ButtonSizes, SizeTokens> = {
    medium: {
      gap: '200',
      paddingBlockStart: '250',
      paddingBlockEnd: '250',
      iconSize: '200',
      typography: 'heading-50',
      paddingInlineStart: floatingGroup ? '400' : '600',
      paddingInlineEnd: floatingGroup ? '400' : '600',
    },
    small: {
      gap: '200',
      paddingBlockStart: '200',
      paddingBlockEnd: '200',
      iconSize: '100',
      typography: 'detail-100',
      paddingInlineStart: '400',
      paddingInlineEnd: '400',
    },
    'extra-small': {
      gap: '100',
      paddingBlockStart: '100',
      paddingBlockEnd: '100',
      iconSize: '100',
      typography: 'detail-100',
      paddingInlineStart: floatingGroup ? '400' : '300',
      paddingInlineEnd: floatingGroup ? '400' : '300',
    },
  };

  // Specific adjustments for 'transparent' use
  const transparentAdjustments: Record<
    ButtonSizes,
    Pick<SizeTokens, 'paddingInlineStart' | 'paddingInlineEnd'>
  > = {
    medium: {
      paddingInlineStart: '300',
      paddingInlineEnd: '300',
    },
    small: {
      paddingInlineStart: '200',
      paddingInlineEnd: '200',
    },
    'extra-small': {
      paddingInlineStart: '100',
      paddingInlineEnd: '100',
    },
  };

  // Specific adjustments for 'form' use
  const formAdjustments: Record<
    ButtonSizes,
    Pick<SizeTokens, 'typography' | 'paddingInlineStart' | 'paddingInlineEnd'>
  > = {
    medium: {
      typography: 'body-200',
      paddingInlineStart: '300',
      paddingInlineEnd: '400',
    },
    small: {
      typography: 'detail-200',
      paddingInlineStart: '200',
      paddingInlineEnd: '300',
    },
    'extra-small': {
      typography: 'detail-200',
      paddingInlineStart: '100',
      paddingInlineEnd: '200',
    },
  };

  if (use === 'transparent') {
    tokens[size] = {
      ...tokens[size],
      ...transparentAdjustments[size],
    };
  }

  if (isFormUse(use)) {
    tokens[size] = {
      ...tokens[size],
      ...formAdjustments[size],
      width: '100%',
    };
  }

  if (isLinkUse(use)) {
    tokens[size] = {
      ...tokens[size],
      paddingBlockStart: '0',
      paddingInlineEnd: '0',
      paddingBlockEnd: '0',
      paddingInlineStart: '0',
    };
  }

  return tokens[size];
};

const LINK_LIKE_USES = ['transparent', 'unstyled'] as const;

export const isLinkLikeUse = (use: string) =>
  isLinkUse(use as ButtonUses) ||
  LINK_LIKE_USES.includes(use as (typeof LINK_LIKE_USES)[number]);

/**
 * Computes the final button use based on the provided use and toolbar context.
 */
export const getComputedUse = ({
  use,
  isInFilledToolbar,
  isTrellis,
}: {
  use: ButtonUses;
  isInFilledToolbar: boolean;
  isTrellis: boolean;
}): ButtonUses => {
  if (isInFilledToolbar) {
    // we always want to use link-on-fill or control-on-fill in a toolbar unless it's *-danger
    // because we're no longer clobbering link styles in toolbars, this needs to work across themes
    if (isLinkUse(use) && !use.includes('danger')) {
      return isControlUse(use) ? 'control-on-fill' : 'link-on-fill';
    }

    // we want new secondary-alt buttons to render as secondary-ghost in canvas
    // secondary-alt (lavender) buttons are inverted in Canvas (orange on white) and look bad against dark toolbar fills
    if (!isTrellis) {
      if (use === 'secondary-alt') {
        return 'secondary-ghost';
      }
      return use;
    }

    // Custom mapping for buttons in a toolbar only in the Trellis theme
    // These mappings are defined by UX.
    // This is a temporary solution to avoid a `Button` `use` codemod/migration for buttons used in a toolbar.
    if (isTrellis) {
      switch (use) {
        case 'primary':
          return 'primary-on-fill';
        case 'secondary':
          return 'secondary-alt';
        case 'secondary-ghost':
          return 'tertiary-on-fill';
        case 'tertiary':
          return 'tertiary-on-fill';
        default:
          return use;
      }
    }
  }

  return use;
};

export const getControlMapping = (use: ButtonUses): ButtonUses => {
  switch (use) {
    case 'link':
      return 'control';
    case 'link-on-bright':
      return 'control-on-bright';
    case 'link-on-dark':
      return 'control-on-dark';
    case 'link-danger':
      return 'control-danger';
    case 'link-on-fill':
      return 'control-on-fill';
    default:
      return use;
  }
};
